import {
  RECTANGULAR_SHAPE,
  type ConfigurationShape,
  type Outline,
  type Slope,
} from "../../../http/deckDesigner/types/configurations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import type { PlaneCoordinate } from "./CartesianPlane";
import {
  DEFAULT_L_SHAPE_CUTOUT_CORNER,
  DEGREES_TO_RADIANS,
  getLShapeCoordinates,
  getLShapeDimensions,
  getLShapeGeometry,
  getRectangularCoordinates,
  getRectangularDimensions,
  METERS_TO_MILLIMETERS,
} from "./deckGeometry";

// Mirrors OutlineVersion in internal/engines/deck_calculator/geometry/outline.go - keep both in sync
// if the convention ever changes. Version 1:
//   - coordinates in integer millimeters, x to the right and y up (as in CartesianPlane), with the
//     origin at the outline's minimum x and minimum y, so every coordinate is >= 0;
//   - vertex indexes 0..n-1 counter-clockwise, vertex 0 being the one with minimum x and, on a tie,
//     minimum y;
//   - one edge per side, from vertex i to vertex i+1 (the last one back to vertex 0).
// The plane can keep centering the deck on screen: these are only the saved coordinates.
export const OUTLINE_VERSION = 1;

export type DeckFootprint = Pick<
  DeckDesignerConfigurationFormValues,
  "shape" | "base" | "height" | "secondaryBase" | "secondaryHeight" | "lShapeCutoutCorner"
>;

// The deck outline on the plane (meters, centered on the origin), from the form's dimensions - empty
// while they're incomplete. Same shape fallback as getLShapeCoordinates.
export const getFootprintCoordinates = ({
  shape,
  base,
  height,
  secondaryBase,
  secondaryHeight,
  lShapeCutoutCorner,
}: DeckFootprint): PlaneCoordinate[] =>
  shape === RECTANGULAR_SHAPE
    ? getRectangularCoordinates(base, height)
    : getLShapeCoordinates(base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner);

// The outline's frame has its origin at the outline's minimum x and y (see buildOutline), wherever
// the plane draws the deck: this is that origin in the plane's coordinates.
const getOutlineOrigin = (coordinates: PlaneCoordinate[]): PlaneCoordinate => ({
  x: Math.min(...coordinates.map((coordinate) => coordinate.x)),
  y: Math.min(...coordinates.map((coordinate) => coordinate.y)),
});

// The outline to save for the form's dimensions, or null while they don't describe a complete shape.
export const buildOutline = (footprint: DeckFootprint): Outline | null => {
  const coordinates = getFootprintCoordinates(footprint);

  if (coordinates.length !== (footprint.shape === RECTANGULAR_SHAPE ? 4 : 6)) {
    return null;
  }

  const origin = getOutlineOrigin(coordinates);
  let points = coordinates.map((coordinate) => ({
    xMm: Math.round((coordinate.x - origin.x) * METERS_TO_MILLIMETERS),
    yMm: Math.round((coordinate.y - origin.y) * METERS_TO_MILLIMETERS),
  }));

  const doubleSignedArea = points.reduce((area, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return area + point.xMm * next.yMm - next.xMm * point.yMm;
  }, 0);
  if (doubleSignedArea < 0) {
    points = points.reverse();
  }

  const firstIndex = points.reduce(
    (best, point, index) =>
      point.xMm < points[best]!.xMm || (point.xMm === points[best]!.xMm && point.yMm < points[best]!.yMm)
        ? index
        : best,
    0,
  );
  points = [...points.slice(firstIndex), ...points.slice(0, firstIndex)];

  return {
    version: OUTLINE_VERSION,
    vertices: points.map((point, index) => ({ index, ...point })),
    edges: points.map((_, index) => ({ from: index, to: (index + 1) % points.length })),
  };
};

// The form's dimensions (meters) and L orientation derived from a saved outline - the inverse of
// buildOutline. Without an outline the dimensions are empty and the L keeps the default orientation.
export const getFootprintFromOutline = (
  shape: ConfigurationShape | null,
  outline: Outline | null,
): Omit<DeckFootprint, "shape"> => {
  const coordinates = [...(outline?.vertices ?? [])]
    .sort((first, second) => first.index - second.index)
    .map((vertex) => ({ x: vertex.xMm / METERS_TO_MILLIMETERS, y: vertex.yMm / METERS_TO_MILLIMETERS }));

  if (shape !== RECTANGULAR_SHAPE) {
    const geometry = getLShapeGeometry(coordinates);

    if (geometry) {
      const dimensions = getLShapeDimensions(coordinates);
      return {
        base: dimensions.width,
        height: dimensions.height,
        secondaryBase: dimensions.secondaryWidth,
        secondaryHeight: dimensions.secondaryHeight,
        lShapeCutoutCorner: geometry.cutoutCorner,
      };
    }
  }

  if (shape === RECTANGULAR_SHAPE && coordinates.length === 4) {
    const dimensions = getRectangularDimensions(coordinates);
    return {
      base: dimensions.width,
      height: dimensions.height,
      secondaryBase: null,
      secondaryHeight: null,
      lShapeCutoutCorner: DEFAULT_L_SHAPE_CUTOUT_CORNER,
    };
  }

  return {
    base: null,
    height: null,
    secondaryBase: null,
    secondaryHeight: null,
    lShapeCutoutCorner: DEFAULT_L_SHAPE_CUTOUT_CORNER,
  };
};

// The arrow of a saved Slope, in the outline's frame (mm).
export type SlopeArrow = Omit<Slope, "slopeDegrees">;

export type TerrainSlope = {
  slopeDegrees: number | null;
  slopeArrow: SlopeArrow | null;
};

// Flat until both the slope and its arrow are set (no slope is saved otherwise).
export const isSlopedTerrain = ({ slopeDegrees, slopeArrow }: TerrainSlope): boolean =>
  slopeDegrees != null && slopeDegrees > 0 && slopeArrow != null;

// The uphill direction of the arrow: degrees from +x, counter-clockwise, 0 <= θ < 360 - null without
// an arrow. Mirrors SlopeDirectionDegrees in geometry/outline.go.
export const getSlopeDirectionDegrees = (arrow: SlopeArrow | null): number | null => {
  if (!arrow) {
    return null;
  }

  const degrees = Math.atan2(arrow.endYMm - arrow.startYMm, arrow.endXMm - arrow.startXMm) / DEGREES_TO_RADIANS;
  return ((degrees % 360) + 360) % 360;
};

// The saved arrow on the plane, for the deck drawn at coordinates - null without them. The saved arrow
// points uphill (as the server expects), the drawn one downhill: start and end are swapped here.
export const getSlopeArrowCoordinates = (
  coordinates: PlaneCoordinate[],
  arrow: SlopeArrow,
): { start: PlaneCoordinate; end: PlaneCoordinate } | null => {
  if (coordinates.length === 0) {
    return null;
  }

  const origin = getOutlineOrigin(coordinates);
  const toPlane = (xMm: number, yMm: number) => ({
    x: origin.x + xMm / METERS_TO_MILLIMETERS,
    y: origin.y + yMm / METERS_TO_MILLIMETERS,
  });

  return { start: toPlane(arrow.endXMm, arrow.endYMm), end: toPlane(arrow.startXMm, arrow.startYMm) };
};

// The inverse of getSlopeArrowCoordinates: the arrow to save for one drawn on the plane.
export const toSlopeArrow = (
  coordinates: PlaneCoordinate[],
  start: PlaneCoordinate,
  end: PlaneCoordinate,
): SlopeArrow => {
  const origin = getOutlineOrigin(coordinates);
  const toMm = (value: number, originValue: number) => Math.round((value - originValue) * METERS_TO_MILLIMETERS);

  return {
    startXMm: toMm(end.x, origin.x),
    startYMm: toMm(end.y, origin.y),
    endXMm: toMm(start.x, origin.x),
    endYMm: toMm(start.y, origin.y),
  };
};

// How far (mm) the outline's vertices spread along the uphill direction: the terrain rises by this
// times tan(slope) from the deck's lowest vertex to its highest one.
export const getUphillSpanMm = (coordinates: PlaneCoordinate[], directionDegrees: number): number => {
  const projections = coordinates.map(
    (coordinate) =>
      coordinate.x * Math.cos(directionDegrees * DEGREES_TO_RADIANS) +
      coordinate.y * Math.sin(directionDegrees * DEGREES_TO_RADIANS),
  );

  return projections.length > 0 ? (Math.max(...projections) - Math.min(...projections)) * METERS_TO_MILLIMETERS : 0;
};

// The terrain rise (mm) at a point, measured from the deck's lowest vertex along the uphill
// direction - where totalHeight (H) applies. Any coordinate origin works, as long as the point and
// the outline share it. Mirrors the riseAt in MaxSlopeRiseMm (geometry/outline.go).
export const getTerrainRiseMmAt = (
  coordinates: PlaneCoordinate[],
  terrainSlope: TerrainSlope,
): ((point: PlaneCoordinate) => number) => {
  if (!isSlopedTerrain(terrainSlope) || coordinates.length === 0) {
    return () => 0;
  }

  const directionRadians = getSlopeDirectionDegrees(terrainSlope.slopeArrow)! * DEGREES_TO_RADIANS;
  const project = (point: PlaneCoordinate) =>
    point.x * Math.cos(directionRadians) + point.y * Math.sin(directionRadians);
  const lowestProjection = Math.min(...coordinates.map(project));
  const tan = Math.tan(terrainSlope.slopeDegrees! * DEGREES_TO_RADIANS);

  return (point) => (project(point) - lowestProjection) * METERS_TO_MILLIMETERS * tan;
};

// How much the terrain rises (mm) at the deck's highest vertex. Mirrors MaxSlopeRiseMm in
// geometry/outline.go.
export const maxSlopeRiseMm = (coordinates: PlaneCoordinate[], terrainSlope: TerrainSlope): number =>
  isSlopedTerrain(terrainSlope)
    ? getUphillSpanMm(coordinates, getSlopeDirectionDegrees(terrainSlope.slopeArrow)!) *
      Math.tan(terrainSlope.slopeDegrees! * DEGREES_TO_RADIANS)
    : 0;
